import { Config } from '@/config';
import type { FileInfo } from '@/services/types/file.types';
import type { Metadata } from '@/services/types/file-uploader.types';

export const dataURLtoBlob = (dataurl: string): Blob => {
  const arr = dataurl.split(',');
  const mime = arr[0].match(/:(.*?);/);
  if (!mime) {
    throw new Error('Invalid MIME type in Data URL');
  }
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new Blob([u8arr], { type: mime[1] });
};

export const getImageUrl = (file?: FileInfo | null): string | null => {
  if (!file?.url) return null;
  if (file.url.startsWith('blob:') || file.url.startsWith('data:')) return file.url;
  if (!file?.metadata?.image) return file.url;
  const image = file.metadata.image;
  const options = [];
  const url = encodeURIComponent(file.url);
  switch (image.type) {
    case 'crop':
      if (image.coordinates) {
        options.push(`g:nowe:${image.coordinates.left}:${image.coordinates.top}`);
        options.push(`c:${image.coordinates.width}:${image.coordinates.height}`);
      }
      if (image.size) {
        options.push(`s:${image.size.width}:${image.size.height}`);
      }
      break;
    case 'raw':
    default:
      options.push('raw:1');
      break;
  }
  options.push('plain');
  return `${Config.images.url}/_/${options.join('/')}/${url}`;
};

// A crop saved without a new file carries only its coordinates and size. It makes the image a crop, whatever it was
// stored as: `getImageUrl` draws a raw image whole, and would ignore the coordinates.
export const mergeImageCrop = (fileInfo: FileInfo, metadata: Metadata) => ({
  ...fileInfo,
  metadata: {
    ...fileInfo.metadata,
    image: {
      ...fileInfo.metadata?.image,
      ...metadata.image,
      type: 'crop' as const,
    },
  },
});
